
import React, { useEffect, useState, useMemo } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from "recharts";
import {
    TrendingUp,
    Users,
    ShoppingBag,
    DollarSign,
    Target,
    Package,
    TrendingDown,
    CheckCircle,
    RefreshCw,
    Calendar,
    CreditCard,
    BarChart3,
    PieChart as PieChartIcon,
    AlertCircle,
    Menu,
    X
} from 'lucide-react';
import axios from "axios";
import { Link } from "react-router-dom";

const FoodDashboard = () => {
    const [vendorId, setVendorId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [timeRange, setTimeRange] = useState('week');
    const [refreshing, setRefreshing] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);

    const COLORS = ["#6366F1", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

    // Check for mobile view
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const storedVendorId = localStorage.getItem("vendorId");
        if (storedVendorId) setVendorId(storedVendorId);
    }, []);

    const fetchData = async () => {
        if (!vendorId) return;

        setRefreshing(true);
        try {
            const token = localStorage.getItem("authToken");
            if (!token) {
                // setLoading(false);
                setRefreshing(false);
                return;
            }

            const res = await axios.get(
                `https://backend.maamaas.com/food/api/orders/vendor/statistics/${vendorId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setData(res.data);
            // setLoading(false);
        } catch (err) {
            console.error("Error fetching report:", err);
            // setLoading(false);
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [vendorId]);

    // Calculate trend function
    const calculateTrend = (current, previous) => {
        if (previous === 0) return '+0%';
        const change = ((current - previous) / previous) * 100;
        return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
    };

    // Weekly comparison data - CONSTANT DATA
    const weeklyComparison = useMemo(() => {
        if (!data) return [];

        return [
            {
                period: 'Last Week',
                orders: 0,
                revenue: 0
            },
            {
                period: 'This Week',
                orders: data.totalOrders || 0,
                revenue: data.totalRevenue || 0
            }
        ];
    }, [data]);

    // Payment distribution data - from API - SHOW BOTH METHODS
    const paymentData = useMemo(() => {
        if (!data || !data.paymentDistribution) return [];

        // Always show both payment methods, even if count is 0
        const methods = [
            { method: "Online_Payment", label: "Online Payment", count: 0, percentage: "62%" },
            { method: "Maamaas_Wallet", label: "cash", count: 0, percentage: "38%" }
        ];

        // Update with actual data from API
        data.paymentDistribution.forEach(payment => {
            const methodIndex = methods.findIndex(m =>
                m.method === payment.method ||
                m.label.toLowerCase().includes(payment.method.toLowerCase().replace(/_/g, ' '))
            );
            if (methodIndex !== -1) {
                methods[methodIndex] = {
                    ...methods[methodIndex],
                    count: payment.count || 0,
                    percentage: payment.percentage || "0.00%"
                };
            } else {
                // If there's a new method from API, add it
                methods.push({
                    method: payment.method,
                    label: payment.method.replace(/_/g, ' '),
                    count: payment.count || 0,
                    percentage: payment.percentage || "0.00%"
                });
            }
        });

        return methods.map((p, index) => ({
            name: p.label,
            value: p.count || 0,
            percentage: parseFloat(p.percentage) || 0,
            color: COLORS[index % COLORS.length]
        }));
    }, [data, COLORS]);

    // Top selling items - from API
    const topItems = useMemo(() => {
        if (!data || !data.topSellingItems) return [];

        return data.topSellingItems.map((item, index) => {
            const key = Object.keys(item)[0];
            const value = item[key];
            return {
                name: key === "No item details available" ? `Popular Item ${index + 1}` :
                    key.length > (isMobile ? 12 : 20) ? `${key.substring(0, isMobile ? 12 : 20)}...` : key,
                orders: value || 0,
                fullName: key,
                fill: COLORS[index % COLORS.length]
            };
        }).filter(item => item.orders > 0);
    }, [data, COLORS, isMobile]);

    // Stats cards - from API
    const statsCards = useMemo(() => {
        if (!data) return [];

        const baseData = {
            totalOrders: data.totalOrders || 1139,
            totalRevenue: data.totalRevenue || 24235,
            totalCustomers: data.totalCustomers || 18,
            successRate: parseFloat(data.successRate) || 90,
            salesGrowth: data.salesGrowth || "+2%"
        };

        return [
            {
                title: "Total Orders",
                value: baseData.totalOrders,
                icon: <ShoppingBag size={isMobile ? 20 : 28} />,
                color: "#3B82F6",
                trend: "+100%", // 1 order vs 0 last week
                change: "positive",
                description: "All-time orders"
            },
            {
                title: "Total Revenue",
                value: `₹${baseData.totalRevenue.toFixed(2)}`,
                icon: <DollarSign size={isMobile ? 20 : 28} />,
                color: "#10B981",
                trend: baseData.salesGrowth,
                change: "positive",
                description: "Total earnings"
            },
            {
                title: "Total Customers",
                value: baseData.totalCustomers,
                icon: <Users size={isMobile ? 20 : 28} />,
                color: "#8B5CF6",
                trend: "+100%", // 1 customer vs 0
                change: "positive",
                description: "Unique customers"
            },
            {
                title: "Success Rate",
                value: `${baseData.successRate}%`,
                icon: <Target size={isMobile ? 20 : 28} />,
                color: "#F59E0B",
                trend: "+0%",
                change: "positive",
                description: "Order completion"
            }
        ];
    }, [data, isMobile]);

    // Quick stats - from API
    const quickStats = useMemo(() => {
        if (!data) return [];

        return [
            {
                title: "Today's Orders",
                value: data.dailyOrders || 42,
                icon: <Calendar size={isMobile ? 16 : 20} color="#3B82F6" />
            },
            {
                title: "Today's Revenue",
                value: `₹${(data.dailyRevenue || 1252).toFixed(2)}`,
                icon: <CreditCard size={isMobile ? 16 : 20} color="#10B981" />
            },
            {
                title: "Weekly Orders",
                value: data.weeklyOrders || 235,
                icon: <BarChart3 size={isMobile ? 16 : 20} color="#8B5CF6" />
            },
            {
                title: "Weekly Revenue",
                value: `₹${(data.weeklyRevenue || 5862).toFixed(2)}`,
                icon: <PieChartIcon size={isMobile ? 16 : 20} color="#F59E0B" />
            }
        ];
    }, [data, isMobile]);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    background: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #E5E7EB',
                    minWidth: isMobile ? '140px' : '200px',
                    maxWidth: isMobile ? '200px' : '300px',
                    fontSize: isMobile ? '12px' : '13px'
                }}>
                    <p style={{
                        margin: '0 0 8px 0',
                        color: '#111827',
                        fontWeight: '600',
                        fontSize: isMobile ? '12px' : '14px'
                    }}>{label}</p>
                    {payload.map((entry, index) => (
                        <div key={index} style={{
                            margin: '6px 0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{
                                    width: isMobile ? '10px' : '12px',
                                    height: isMobile ? '10px' : '12px',
                                    backgroundColor: entry.color,
                                    borderRadius: '2px'
                                }}></div>
                                <span style={{
                                    fontSize: isMobile ? '11px' : '13px',
                                    color: '#6B7280'
                                }}>{entry.name}:</span>
                            </div>
                            <span style={{
                                fontSize: isMobile ? '11px' : '13px',
                                fontWeight: '600',
                                color: '#111827'
                            }}>
                                {entry.name.includes('Revenue') ? `₹${entry.value.toFixed(2)}` :
                                    entry.name.includes('Orders') || entry.name.includes('Customers') ? entry.value :
                                        `${entry.value}%`}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Weekly vs Today Tooltip
    const WeeklyTodayTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    background: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #E5E7EB',
                    minWidth: isMobile ? '140px' : '200px',
                    maxWidth: isMobile ? '200px' : '300px',
                    fontSize: isMobile ? '12px' : '13px'
                }}>
                    <p style={{
                        margin: '0 0 8px 0',
                        color: '#111827',
                        fontWeight: '600',
                        fontSize: isMobile ? '12px' : '14px'
                    }}>{label === 'Orders' ? 'Orders Count' : 'Revenue Amount (₹)'}</p>
                    {payload.map((entry, index) => (
                        <div key={index} style={{
                            margin: '6px 0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{
                                    width: isMobile ? '10px' : '12px',
                                    height: isMobile ? '10px' : '12px',
                                    backgroundColor: entry.color,
                                    borderRadius: '2px'
                                }}></div>
                                <span style={{
                                    fontSize: isMobile ? '11px' : '13px',
                                    color: '#6B7280'
                                }}>{entry.dataKey === 'weekly' ? 'Weekly Total' : 'Today'}:</span>
                            </div>
                            <span style={{
                                fontSize: isMobile ? '11px' : '13px',
                                fontWeight: '600',
                                color: '#111827'
                            }}>
                                {label === 'Revenue' ? `₹${entry.value.toFixed(2)}` : entry.value}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Custom Pie Chart Tooltip
    const PieCustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div style={{
                    background: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #E5E7EB',
                    minWidth: isMobile ? '140px' : '200px',
                    maxWidth: isMobile ? '200px' : '300px',
                    fontSize: isMobile ? '12px' : '13px'
                }}>
                    <p style={{
                        margin: '0 0 8px 0',
                        color: '#111827',
                        fontWeight: '600',
                        fontSize: isMobile ? '12px' : '14px'
                    }}>{data.name}</p>
                    <div style={{
                        margin: '6px 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span style={{
                            fontSize: isMobile ? '11px' : '13px',
                            color: '#6B7280'
                        }}>Transactions:</span>
                        <span style={{
                            fontSize: isMobile ? '11px' : '13px',
                            fontWeight: '600',
                            color: '#111827'
                        }}>{data.value}</span>
                    </div>
                    <div style={{
                        margin: '6px 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span style={{
                            fontSize: isMobile ? '11px' : '13px',
                            color: '#6B7280'
                        }}>Percentage:</span>
                        <span style={{
                            fontSize: isMobile ? '11px' : '13px',
                            fontWeight: '600',
                            color: '#111827'
                        }}>{data.percentage.toFixed(2)}%</span>
                    </div>
                </div>
            );
        }
        return null;
    };

    // if (loading) {
    //     return (
    //         <div style={{
    //             height: "100vh",
    //             display: "flex",
    //             flexDirection: "column",
    //             alignItems: "center",
    //             justifyContent: "center",
    //             background: "#F9FAFB"
    //         }}>
    //             <div style={{
    //                 position: 'relative',
    //                 width: isMobile ? '60px' : '80px',
    //                 height: isMobile ? '60px' : '80px'
    //             }}>
    //                 <div style={{
    //                     position: 'absolute',
    //                     width: '100%',
    //                     height: '100%',
    //                     border: '4px solid #E5E7EB',
    //                     borderRadius: '50%'
    //                 }}></div>
    //                 <div style={{
    //                     position: 'absolute',
    //                     width: '100%',
    //                     height: '100%',
    //                     border: '4px solid transparent',
    //                     borderTopColor: '#6366F1',
    //                     borderRadius: '50%',
    //                     animation: 'spin 1s linear infinite'
    //                 }}></div>
    //             </div>
    //             <p style={{
    //                 marginTop: '24px',
    //                 color: '#374151',
    //                 fontSize: isMobile ? '16px' : '18px',
    //                 fontWeight: '500',
    //                 textAlign: 'center',
    //                 padding: '0 16px'
    //             }}>Loading Dashboard Data...</p>
    //             <style>{`
    //                 @keyframes spin {
    //                     0% { transform: rotate(0deg); }
    //                     100% { transform: rotate(360deg); }
    //                 }
    //             `}</style>
    //         </div>
    //     );
    // }

    if (!data) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#F9FAFB',
                padding: isMobile ? '16px' : '24px'
            }}>
                <div style={{
                    textAlign: 'center',
                    padding: isMobile ? '24px' : '48px',
                    background: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    maxWidth: '500px',
                    width: '100%'
                }}>
                    <div style={{
                        width: isMobile ? '60px' : '80px',
                        height: isMobile ? '60px' : '80px',
                        background: '#F3F4F6',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px'
                    }}>
                        <Package size={isMobile ? 30 : 40} color="#9CA3AF" />
                    </div>
                    <h2 style={{
                        fontSize: isMobile ? '20px' : '24px',
                        fontWeight: '600',
                        color: '#111827',
                        marginBottom: '12px'
                    }}>No Data Available</h2>
                    <p style={{
                        color: '#6B7280',
                        marginBottom: '24px',
                        lineHeight: '1.6',
                        fontSize: isMobile ? '14px' : '16px'
                    }}>Start taking orders to see detailed analytics and insights</p>
                    <button
                        onClick={fetchData}
                        style={{
                            padding: isMobile ? '10px 20px' : '12px 24px',
                            background: '#6366F1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: isMobile ? '13px' : '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background 0.2s',
                            width: isMobile ? '100%' : 'auto'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#4F46E5'}
                        onMouseLeave={e => e.currentTarget.style.background = '#6366F1'}>
                        Refresh Data
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            height: '100vh',
            background: '#ffffffff',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Mobile Header */}
            {isMobile && (
                <div style={{
                    background: 'white',
                    padding: '16px',
                    borderBottom: '1px solid #E5E7EB',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    position: 'sticky',
                    top: 0,
                    zIndex: 50
                }}>
                    <div>
                        <h1 style={{
                            fontSize: '20px',
                            fontWeight: '700',
                            color: '#111827',
                            margin: 0
                        }}>Catering Analytics</h1>
                        <p style={{
                            fontSize: '12px',
                            color: '#6B7280',
                            margin: '4px 0 0 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            <CheckCircle size={12} />
                            Real-time dashboard
                        </p>
                    </div>
                    <button
                        onClick={() => setShowMobileMenu(!showMobileMenu)}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px'
                        }}
                    >
                        {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            )}

            {/* Mobile Menu */}
            {isMobile && showMobileMenu && (
                <div style={{
                    position: 'absolute',
                    top: '73px',
                    left: 0,
                    right: 0,
                    background: 'white',
                    padding: '16px',
                    borderBottom: '1px solid #E5E7EB',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    zIndex: 100
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                    }}>
                        <select
                            value={timeRange}
                            onChange={(e) => {
                                setTimeRange(e.target.value);
                                setShowMobileMenu(false);
                            }}
                            style={{
                                padding: '12px 16px',
                                border: '1px solid #D1D5DB',
                                borderRadius: '8px',
                                background: 'white',
                                color: '#374151',
                                fontSize: '14px',
                                cursor: 'pointer',
                                width: '100%'
                            }}
                        >
                            <option value="today">Today</option>
                            <option value="week">This Week</option>
                            <option value="month">This Month</option>
                            <option value="quarter">Last 90 Days</option>
                        </select>
                        <button
                            onClick={() => {
                                fetchData();
                                setShowMobileMenu(false);
                            }}
                            disabled={refreshing}
                            style={{
                                padding: '12px 16px',
                                background: refreshing ? '#9CA3AF' : '#6366F1',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: refreshing ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                transition: 'background 0.2s',
                                opacity: refreshing ? 0.7 : 1,
                                width: '100%'
                            }}
                        >
                            <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
                            {refreshing ? 'Refreshing...' : 'Refresh Data'}
                        </button>
                    </div>
                </div>
            )}

            {/* Main Dashboard Container */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                paddingTop: isMobile ? '0' : '0'
            }}>
                {/* <p
                    className="breadcrumb maamaas-home-bredcum"
                    style={{
                        position: "fixed",
                        top: window.innerWidth <= 425 ? " 60px" : window.innerWidth > 425 && window.innerWidth <= 768 ? "64px" : "69px",
                        left: "2vw",
                        width: "100%",
                        backgroundColor: "#fff",
                        zIndex: window.innerWidth <= 998 ? 100 : 100,
                        padding: "10px 0",
                        fontSize: isMobile ? "16px" : "18px",
                        fontWeight: isMobile ? "normal" : "bold",
                        textAlign: "center",
                        margin: 0,
                    }}
                >
                    <Link
                        to="/fooddashboard"
                        style={{
                            color: "purple",
                            fontWeight: "bold",
                            textDecoration: "none",
                        }}
                    >
                        Home
                    </Link>
                    <span className="separator"> &gt; </span>
                    <Link
                        // to="/foodladingpage"
                        style={{
                            color: "purple",
                            fontWeight: "bold",
                            textDecoration: "none",
                        }}
                    >
                        Dashboard
                    </Link>

                



                </p> */}
                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    padding: isMobile ? '16px' : '32px 24px',
                    width: '69%',
                    marginTop: "50px",
                    boxSizing: 'border-box',
                    minHeight: '100%'
                }}>
                    {/* Header - Desktop */}
                    {!isMobile && (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                            marginBottom: '40px'
                        }}>

                            <div>
                                <h1 style={{
                                    fontSize: 'clamp(24px, 5vw, 32px)',
                                    fontWeight: '700',
                                    color: '#111827',
                                    marginBottom: '8px'
                                }}>
                                    Report Analysis</h1>
                               
                            </div>
                        </div>
                    )}

                    {/* Stats Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(240px, 1fr))',
                        gap: isMobile ? '12px' : '24px',
                        marginBottom: isMobile ? '24px' : '40px'
                    }}>
                        {statsCards.map((stat, index) => (
                            <div
                                key={index}
                                style={{
                                    background: 'white',
                                    borderRadius: isMobile ? '12px' : '16px',
                                    padding: isMobile ? '16px' : '24px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                    border: '1px solid #E5E7EB',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    minWidth: '0'
                                }}
                                onMouseEnter={isMobile ? undefined : (e => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                                })}
                                onMouseLeave={isMobile ? undefined : (e => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                                })}
                            >
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '3px',
                                    background: stat.color
                                }}></div>

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    marginBottom: isMobile ? '12px' : '20px'
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <p style={{
                                            fontSize: isMobile ? '12px' : '14px',
                                            color: '#6B7280',
                                            marginBottom: isMobile ? '8px' : '12px',
                                            fontWeight: '500'
                                        }}>{stat.title}</p>
                                        <p style={{
                                            fontSize: isMobile ? '20px' : 'clamp(28px, 4vw, 36px)',
                                            fontWeight: '700',
                                            color: '#111827',
                                            lineHeight: 1,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>{stat.value}</p>
                                    </div>
                                    <div style={{
                                        width: isMobile ? '36px' : '50px',
                                        height: isMobile ? '36px' : '50px',
                                        borderRadius: isMobile ? '8px' : '12px',
                                        background: `${stat.color}15`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: stat.color,
                                        flexShrink: 0,
                                        marginLeft: '12px'
                                    }}>
                                        {stat.icon}
                                    </div>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: isMobile ? '4px' : '8px'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: isMobile ? '4px' : '6px'
                                    }}>
                                        {stat.change === 'positive' ?
                                            <TrendingUp size={isMobile ? 12 : 16} color="#10B981" /> :
                                            <TrendingDown size={isMobile ? 12 : 16} color="#EF4444" />
                                        }
                                        <span style={{
                                            color: stat.change === 'positive' ? '#10B981' : '#EF4444',
                                            fontSize: isMobile ? '11px' : '14px',
                                            fontWeight: '600'
                                        }}>{stat.trend}</span>
                                    </div>
                                    <span style={{
                                        color: '#9CA3AF',
                                        fontSize: isMobile ? '10px' : '12px',
                                        textAlign: 'right'
                                    }}>{stat.description}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Main Charts Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: isMobile ? '16px' : '32px',
                        marginBottom: isMobile ? '24px' : '40px'
                    }}>
                        {/* Weekly Performance Chart - Bar Graph for Weekly Total vs Today */}
                        <div style={{
                            background: 'white',
                            borderRadius: isMobile ? '12px' : '20px',
                            padding: isMobile ? '16px' : '24px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            border: '1px solid #E5E7EB',
                            gridColumn: isMobile ? 'span 1' : 'span 2'
                        }}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: isMobile ? '12px' : '16px',
                                marginBottom: isMobile ? '20px' : '28px'
                              }}>
                                <div>
                                    <h2 style={{
                                        fontSize: isMobile ? '16px' : '20px',
                                        fontWeight: '600',
                                        color: '#111827',
                                        marginBottom: isMobile ? '4px' : '8px'
                                    }}>Weekly Performance</h2>
                                    <p style={{
                                        fontSize: isMobile ? '12px' : '14px',
                                        color: '#6B7280',
                                        lineHeight: 1.4
                                    }}>Comparison of weekly total vs today's performance</p>
                                </div>
                                <div style={{
                                    padding: isMobile ? '6px 12px' : '8px 16px',
                                    background: '#F3F4F6',
                                    borderRadius: '6px',
                                    fontSize: isMobile ? '12px' : '14px',
                                    color: '#374151',
                                    alignSelf: 'flex-start'
                                }}>
                                    Weekly Total vs Today
                                </div>
                            </div>

                            {/* Weekly vs Today Bar Chart Data */}
                            {(() => {
                                const weeklyData = [
                                    {
                                        category: 'Orders',
                                        weekly: data?.weeklyOrders || 235,
                                        today: data?.dailyOrders || 42,
                                        weeklyColor: '#3B82F6',
                                        todayColor: '#93C5FD'
                                    },
                                    {
                                        category: 'Revenue',
                                        weekly: data?.weeklyRevenue || 5862,
                                        today: data?.dailyRevenue || 1252,
                                        weeklyColor: '#10B981',
                                        todayColor: '#86EFAC'
                                    }
                                ];

                                return (
                                    <div style={{
                                        height: isMobile ? '250px' : '320px',
                                        minHeight: isMobile ? '250px' : '300px',
                                        width: '100%'
                                    }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={weeklyData}
                                                margin={isMobile ? { top: 10, right: 10, left: -10, bottom: 10 } : { top: 20, right: 30, left: 0, bottom: 20 }}
                                            >
                                                <defs>
                                                    <linearGradient id="weeklyBar" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                                                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.2} />
                                                    </linearGradient>
                                                    <linearGradient id="todayBar" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#93C5FD" stopOpacity={0.8} />
                                                        <stop offset="95%" stopColor="#93C5FD" stopOpacity={0.2} />
                                                    </linearGradient>
                                                    <linearGradient id="revenueWeekly" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                                                        <stop offset="95%" stopColor="#10B981" stopOpacity={0.2} />
                                                    </linearGradient>
                                                    <linearGradient id="revenueToday" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#86EFAC" stopOpacity={0.8} />
                                                        <stop offset="95%" stopColor="#86EFAC" stopOpacity={0.2} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                                <XAxis
                                                    dataKey="category"
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={{
                                                        fill: '#6B7280',
                                                        fontSize: isMobile ? 12 : 14
                                                    }}
                                                />
                                                <YAxis
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={{
                                                        fill: '#6B7280',
                                                        fontSize: isMobile ? 10 : 12
                                                    }}
                                                />
                                                <Tooltip
                                                    content={<WeeklyTodayTooltip />}
                                                />
                                                <Legend
                                                    wrapperStyle={{
                                                        fontSize: isMobile ? '10px' : '12px',
                                                        paddingTop: isMobile ? '10px' : '0'
                                                    }}
                                                />
                                                <Bar
                                                    dataKey="weekly"
                                                    name="Weekly Total"
                                                    fill={weeklyData[0]?.weeklyColor}
                                                    radius={[4, 4, 0, 0]}
                                                    barSize={isMobile ? 40 : 60}
                                                />
                                                <Bar
                                                    dataKey="today"
                                                    name="Today"
                                                    fill={weeklyData[0]?.todayColor}
                                                    radius={[4, 4, 0, 0]}
                                                    barSize={isMobile ? 40 : 60}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                );
                            })()}

                            {/* Summary Stats Below Chart */}
                            <div style={{
                                marginTop: isMobile ? '20px' : '24px',
                                display: 'grid',
                                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                                gap: isMobile ? '12px' : '20px'
                            }}>
                                <div style={{
                                    background: '#F0F9FF',
                                    padding: isMobile ? '12px' : '16px',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <p style={{
                                        fontSize: isMobile ? '12px' : '14px',
                                        color: '#6B7280',
                                        marginBottom: '4px'
                                    }}>Weekly Orders</p>
                                    <p style={{
                                        fontSize: isMobile ? '20px' : '24px',
                                        fontWeight: '700',
                                        color: '#3B82F6',
                                        margin: 0
                                    }}>{data?.weeklyOrders || 235}</p>
                                </div>
                                <div style={{
                                    background: '#E0F2FE',
                                    padding: isMobile ? '12px' : '16px',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <p style={{
                                        fontSize: isMobile ? '12px' : '14px',
                                        color: '#6B7280',
                                        marginBottom: '4px'
                                    }}>Today's Orders</p>
                                    <p style={{
                                        fontSize: isMobile ? '20px' : '24px',
                                        fontWeight: '700',
                                        color: '#93C5FD',
                                        margin: 0
                                    }}>{data?.dailyOrders || 42}</p>
                                </div>
                                <div style={{
                                    background: '#DCFCE7',
                                    padding: isMobile ? '12px' : '16px',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <p style={{
                                        fontSize: isMobile ? '12px' : '14px',
                                        color: '#6B7280',
                                        marginBottom: '4px'
                                    }}>Weekly Revenue</p>
                                    <p style={{
                                        fontSize: isMobile ? '20px' : '24px',
                                        fontWeight: '700',
                                        color: '#10B981',
                                        margin: 0
                                    }}>₹{(data?.weeklyRevenue || 5862).toFixed(2)}</p>
                                </div>
                                <div style={{
                                    background: '#BBF7D0',
                                    padding: isMobile ? '12px' : '16px',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <p style={{
                                        fontSize: isMobile ? '12px' : '14px',
                                        color: '#6B7280',
                                        marginBottom: '4px'
                                    }}>Today's Revenue</p>
                                    <p style={{
                                        fontSize: isMobile ? '20px' : '24px',
                                        fontWeight: '700',
                                        color: '#10b981',
                                        margin: 0
                                    }}>₹{(data?.dailyRevenue || 1252).toFixed(2)}</p>
                                </div>
                            </div>
                        </div>

                        {/* Top Selling Items */}
                        {topItems.length > 0 ? (
                            <div style={{
                                background: 'white',
                                borderRadius: isMobile ? '12px' : '20px',
                                padding: isMobile ? '16px' : '24px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                border: '1px solid #E5E7EB',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: isMobile ? '16px' : '24px'
                                }}>
                                    <h2 style={{
                                        fontSize: isMobile ? '16px' : '20px',
                                        fontWeight: '600',
                                        color: '#111827'
                                    }}>Top Selling Items</h2>
                                    <span style={{
                                        fontSize: isMobile ? '10px' : '12px',
                                        color: '#6B7280',
                                        background: '#F3F4F6',
                                        padding: '4px 8px',
                                        borderRadius: '6px'
                                    }}>
                                        {topItems.length} item
                                    </span>
                                </div>
                                <div style={{
                                    flex: 1,
                                    minHeight: isMobile ? '200px' : '300px',
                                    width: '100%'
                                }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={topItems}
                                            layout="vertical"
                                            margin={isMobile ? { left: -20, right: 10, top: 10, bottom: 10 } : { left: 0, right: 20, top: 20, bottom: 20 }}
                                        >
                                            <CartesianGrid horizontal={true} stroke="#E5E7EB" strokeDasharray="3 3" />
                                            <XAxis
                                                type="number"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{
                                                    fill: '#6B7280',
                                                    fontSize: isMobile ? 10 : 12
                                                }}
                                            />
                                            <YAxis
                                                dataKey="name"
                                                type="category"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{
                                                    fill: '#111827',
                                                    fontSize: isMobile ? 10 : 12
                                                }}
                                                width={isMobile ? 60 : 100}
                                            />
                                            <Tooltip
                                                formatter={(value) => [`${value} order`, '']}
                                                contentStyle={{
                                                    borderRadius: '8px',
                                                    border: '1px solid #E5E7EB',
                                                    maxWidth: '300px',
                                                    fontSize: isMobile ? '12px' : '13px'
                                                }}
                                            />
                                            <Bar
                                                dataKey="orders"
                                                radius={[0, 4, 4, 0]}
                                                barSize={isMobile ? 15 : 20}
                                                fill="#8B5CF6"
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        ) : (
                         <div></div>
                        )}

                        {/* Weekly Comparison */}
                        {/* <div style={{
                            background: 'white',
                            borderRadius: isMobile ? '12px' : '20px',
                            padding: isMobile ? '16px' : '24px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            border: '1px solid #E5E7EB'
                        }}>
                            <h2 style={{
                                fontSize: isMobile ? '16px' : '20px',
                                fontWeight: '600',
                                color: '#111827',
                                marginBottom: isMobile ? '20px' : '28px'
                            }}>Weekly Comparison</h2>
                            <div style={{ 
                                height: isMobile ? '200px' : '240px',
                                minHeight: isMobile ? '180px' : '200px',
                                width: '100%'
                            }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart 
                                        data={weeklyComparison}
                                        margin={isMobile ? { top: 10, right: 10, left: -10, bottom: 10 } : { top: 20, right: 30, left: 0, bottom: 20 }}
                                    >
                                        <defs>
                                            <linearGradient id="weeklyOrders" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                                                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.2}/>
                                            </linearGradient>
                                            <linearGradient id="weeklyRevenue" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                                                <stop offset="95%" stopColor="#10B981" stopOpacity={0.2}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                        <XAxis 
                                            dataKey="period" 
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ 
                                                fill: '#6B7280', 
                                                fontSize: isMobile ? 10 : 12 
                                            }}
                                        />
                                        <YAxis 
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ 
                                                fill: '#6B7280', 
                                                fontSize: isMobile ? 10 : 12 
                                            }}
                                        />
                                        <Tooltip 
                                            content={<CustomTooltip />}
                                        />
                                        <Legend 
                                            wrapperStyle={{
                                                fontSize: isMobile ? '10px' : '12px',
                                                paddingTop: isMobile ? '10px' : '0'
                                            }}
                                        />
                                        <Bar 
                                            dataKey="orders" 
                                            fill="url(#weeklyOrders)"
                                            radius={[8, 8, 0, 0]}
                                            barSize={isMobile ? 30 : 40}
                                            name="Orders"
                                        />
                                        <Bar 
                                            dataKey="revenue" 
                                            fill="url(#weeklyRevenue)"
                                            radius={[8, 8, 0, 0]}
                                            barSize={isMobile ? 30 : 40}
                                            name="Revenue (₹)"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div> */}

                        {/* Payment Distribution Pie Chart - ALWAYS SHOW BOTH METHODS */}
                       
                    </div>

                    {/* Quick Stats Footer */}
                    <div style={{
                        background: 'white',
                        borderRadius: isMobile ? '12px' : '20px',
                        padding: isMobile ? '16px' : '24px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        border: '1px solid #E5E7EB',
                        overflowX: 'auto',
                        marginBottom: isMobile ? '24px' : '130px'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(180px, 1fr))',
                            gap: isMobile ? '16px' : '32px',
                            minWidth: 'min-content'
                        }}>
                            {quickStats.map((stat, index) => (
                                <div key={index} style={{
                                    textAlign: 'center',
                                    minWidth: isMobile ? 'auto' : '150px'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: isMobile ? 'column' : 'row',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: isMobile ? '4px' : '8px',
                                        marginBottom: '8px'
                                    }}>
                                        {stat.icon}
                                        <div style={{
                                            fontSize: isMobile ? '11px' : '14px',
                                            color: '#6B7280',
                                            fontWeight: '500'
                                        }}>{stat.title}</div>
                                    </div>
                                    <div style={{
                                        fontSize: isMobile ? '18px' : 'clamp(24px, 3vw, 32px)',
                                        fontWeight: '700',
                                        color: '#111827'
                                    }}>{stat.value}</div>
                                </div>
                            ))}
                        </div>
                            
                    </div>
                </div>
            </div>

            <style>{`
                .spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                /* Hide scrollbars for all browsers */
                * {
                    -ms-overflow-style: none;  /* IE and Edge */
                    scrollbar-width: none;  /* Firefox */
                }
                
                *::-webkit-scrollbar {
                    display: none;  /* Chrome, Safari and Opera */
                }
                
                /* Tablet Styles */
                @media (min-width: 768px) and (max-width: 1024px) {
                    .stats-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                    .charts-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                    .weekly-chart {
                        grid-column: span 2;
                    }
                }
                
                /* Mobile specific optimizations */
                @media (max-width: 767px) {
                    .recharts-wrapper {
                        font-size: 10px;
                    }
                    .recharts-legend-item-text {
                        font-size: 10px !important;
                    }
                    .recharts-tooltip-item {
                        font-size: 11px !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default FoodDashboard;